import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { EXTERNAL_NAME_STORAGE_KEY, IN_ROOM } from 'consts';
import { useManageBackgroundProcessor } from 'features/VirtualBackground/hooks/useManageBackgroundProcessor';
import { useRoomVirtualBackground } from 'features/VirtualBackground/utils/setupVirtualBackgrounds';
import { selectAuthToken, selectCallConfig, selectIsAuthorized } from 'state/selectors';
import { useRoomInfo } from 'useCase/room/useRoomInfo';
import { useSyncTime } from 'useCase/app/useSyncTime';
import { selectLocalVideoTrack } from 'state/selectors/localTracks';
import { useWaitingRoomJoinFlow } from 'features/PreJoin/hooks/useWaitingRoomJoinFlow';
import { useModal } from 'useCase/modal/useModal';
import { ModalName } from 'features/Modals/ModalProvider';
import { useConnectToRoom } from 'features/PreJoin';
import { useUserSettingsContext } from '../../contexts';
import { getFullNameByAuthToken } from '../../util/getFullNameByAuthToken';
import { useBootstrapMeetSession } from './useBootstrapMeetSession';

export const useMainPageViewModel = () => {
    const { roomName } = useParams<{ roomName: string }>();
    const history = useHistory();

    const isGroupCall = selectCallConfig()?.isGroupCall ?? roomName.split(':')[3] === 'GROUP';

    const localVideoTrack = selectLocalVideoTrack();
    useRoomVirtualBackground();
    useManageBackgroundProcessor();

    const { setRoomId } = useRoomInfo();
    const { syncTimeWithServer } = useSyncTime();
    const { videoDeviceId, setVideoDeviceId } = useUserSettingsContext();

    const authToken = selectAuthToken();
    const isAuthorized = selectIsAuthorized();

    const externalName = localStorage.getItem(EXTERNAL_NAME_STORAGE_KEY) || '';
    const initialName = useMemo(
        () => (isAuthorized ? getFullNameByAuthToken(authToken as string) : externalName),
        [isAuthorized, authToken, externalName],
    );

    const [inRoomNow, setInRoomNow] = useState<boolean>(!!sessionStorage.getItem(IN_ROOM));
    const [isEmptyNameError, setIsEmptyNameError] = useState<boolean>(false);
    const [isParticipantsLimitError, setIsParticipantsLimitError] = useState<boolean>(false);

    const { showLoader, prepareDataAndStartRoom } = useBootstrapMeetSession(roomName);
    const { openModal } = useModal();
    const { isUserNameValid } = useConnectToRoom();

    const [name, setName] = useState<string>(initialName);
    const [connectDisabled, setConnectDisabled] = useState<boolean>(!isUserNameValid(initialName));

    const changeRoomConnection = useCallback(() => {
        setInRoomNow(!!sessionStorage.getItem(IN_ROOM));
    }, []);

    const {
        isJoinRejected,
        isParticipantsLimitRetryLoading,
        isShowWaitingPage,
        join: performJoin,
        retryWaitingRoomJoin,
        dismissJoinRejected,
    } = useWaitingRoomJoinFlow({
        mode: 'web',
        roomName,
        getUserName: () => name,
        onEnterRoom: changeRoomConnection,
        onParticipantsLimitError: () => setIsParticipantsLimitError(true),
        onCallNotStarted: () => openModal(ModalName.CALL_NOT_STARTED),
        onUnsupportedP2pJoin: () => history.push('/error/VCS_P2P_JOIN_BY_LINK_OPERATION_NOT_SUPPORTED'),
        prepareVideoDevice: async () => {
            if (!videoDeviceId && localVideoTrack) {
                const deviceId = await localVideoTrack.getDeviceId();
                if (deviceId) setVideoDeviceId(deviceId);
            }
        },
    });

    const connectToRoom = useCallback(
        async (params?: { fromParticipantsLimitRetry?: boolean }) => {
            const result = await performJoin(params);
            if (result && !result.ok && result.reason === 'empty_name') {
                setIsEmptyNameError(true);
            }
        },
        [performJoin],
    );

    const changeUserName = useCallback(({ name }: { name: string }) => {
        setName(name);
        setConnectDisabled(!isUserNameValid(name));
        setIsEmptyNameError(false);
    }, []);

    useEffect(() => {
        syncTimeWithServer().then();
    }, [syncTimeWithServer]);

    useEffect(() => {
        setRoomId(roomName);
        const search = window.location.search;
        window.history.replaceState(null, '', search ? `/${roomName}${search}` : `/${roomName}`);
    }, [roomName, setRoomId]);

    return {
        state: {
            name,
            connectDisabled,
            isEmptyNameError,
            isParticipantsLimitError,
            isParticipantsLimitRetryLoading,
            showLoader,
            inRoomNow,
            isAuthorized,
            isGroupCall,
            isJoinRejected,
            isShowWaitingPage,
        },
        actions: {
            changeUserName,
            connectToRoom,
            retryWaitingRoomJoin,
            retryParticipantsLimit: useCallback(
                () => connectToRoom({ fromParticipantsLimitRetry: true }),
                [connectToRoom],
            ),
            closeParticipantsLimitError: useCallback(() => setIsParticipantsLimitError(false), []),
            changeRoomConnection,
            prepareDataAndStartRoom,
            dismissJoinRejected,
        },
    };
};
